import { type Frame } from '@/karehero-app/tell-us-once/intake/Flow';

/**
 * Onboarding, for people outside the team, as slides: the questions they ask
 * of a design, answered in plain words, a point at a time. Drawn from
 * "Warming up onboarding" and "Warmer onboarding, built": what Steph said,
 * then 2.0 walked through as an ask followed by something given back, then
 * what is still open.
 */
export type WalkthroughSlide = {
  /** Kept in the deck but not shown. */
  hidden?: boolean;
  /** No question means no text: the phone alone, in the middle. */
  question?: string;
  /** A paragraph before the points: the aim, said once, before the detail. */
  intro?: string;
  points?: string[];
  /** Points split under headings, for a slide that compares two things. */
  groups?: { heading: string; points: string[] }[];
  /** The moment the phone jumps to while this slide is up. No phone without one. */
  frame?: Frame;
  /** Which onboarding the phone runs. 2.0 on dev unless a slide says. */
  version?: '1.0' | '1.1' | '2.0-dev';
  /** A small note under the points, for a term the room may not know. */
  footnote?: string;
  /**
   * The research a slide rests on, with where it comes from, and a link when
   * the source is public. A point cites one with `[n]`, counting this slide's
   * research from 1; it opens in the research panel.
   */
  research?: { note: string; source: string; href?: string }[];
  /**
   * A scene of the Financial status form as it ships, in the phone instead of
   * the chat: the kind of form Tell Us Once is replacing.
   */
  form?: string;
  /** Somewhere else in Loft that shows it. */
  link?: { label: string; href: string };
  /**
   * A row of smaller phones, each on its own moment with a caption under it,
   * in place of one phone: the key screens side by side.
   */
  frames?: { frame: Frame; label: string }[];
  /**
   * Another deck's own screen in the phone, by key: the deck passes the
   * screens it uses to the walkthrough. See OnboardingWalkthrough's `screens`.
   */
  screen?: string;
  /** Several of the deck's own screens side by side, each with a caption. */
  screenRow?: { screen: string; label: string }[];
};

export const onboardingWalkthrough = {
  /**
   * Research behind the work that no slide cites, listed after the cited
   * sources. A hidden slide's research joins it.
   */
  moreResearch: [
    {
      note: 'Attendance Allowance is £76.70 to £114.60 a week, from 7 April 2026.',
      source: 'GOV.UK',
      href: 'https://www.gov.uk/attendance-allowance/what-youll-get',
    },
  ] satisfies NonNullable<WalkthroughSlide['research']>,
  slides: [
    {
      // Sets the scene before any screens: where onboarding sits, and why it
      // has changed.
      question: 'Tell us once so far',
      // One page of a form as it is today: where the first iteration started.
      form: 'home-details',
      points: [
        "We've built the technical foundations. Onboarding is the first place we're putting them to use.",
        "It turns the onboarding intake into a conversation. That's the gateway to how the app works with AI.",
        "What you've seen so far was the first iteration: today's form, turned into a chat.",
        "Since then, with feedback and the next round of work, we've made changes to make sure we're heading in the right direction.",
      ],
    },
    {
      // Steph's feedback on 1.0, in her three parts. The rest of the deck
      // answers it.
      question: 'Where we look to improve',
      points: [
        "It asked without giving anything back. It followed a traditional intake, rather than thinking about what they get from each answer. It's meant to be a conversation, so it should feel human.",
        'The words were correct but cold. "Process data", "ID" and "Is that it" read like a form talking.',
        'We added a review step so they could check what the AI picked up. It was needed, but it felt out of sequence.',
        "Now it's time to focus on the UX writing, the tone of voice and the conversational design. [1]",
      ],
      // The separate review step, where nine asks finally got something back.
      frame: 'review-facts',
      version: '1.0',
      research: [
        {
          note: 'Calm and reassuring, personal rather than formal. The tone changes with the moment and how the reader is likely to feel.',
          source: 'NHS service manual',
          href: 'https://service-manual.nhs.uk/content/voice-and-tone',
        },
      ],
    },
    {
      // The shape of 2.0 before any one screen of it. Hidden: the slides
      // before it already cover where it started, and the ones after walk it.
      // Its research still shows in the panel.
      hidden: true,
      question: 'The new shape',
      groups: [
        {
          heading: 'Now',
          points: [
            'One open question, in their own words.',
            'Then something back: what we heard, read back in their own words.',
            'Every answer earns something before the next ask. [1] [2]',
          ],
        },
        {
          heading: 'How it looks',
          points: [
            "We're also trialling some small design changes. Our brand colours are used more subtly, so it feels calmer.",
          ],
        },
      ],
      research: [
        {
          note: "Noom's onboarding runs to over 100 screens and still converts, because every answer builds towards something made for them.",
          source: 'RevenueCat',
          href: 'https://www.revenuecat.com/blog/growth/web-to-app-onboarding-funnel',
        },
        {
          note: 'Headspace turns its quiz into a personal starting point. Calendly asks three things and hands back a working link.',
          source: 'Our Headspace UX scan',
        },
      ],
    },
    {
      question: 'One question, in their own words',
      points: [
        "We ask once who they're looking after and what's happening with their care, with a promise: I'll point you to the right help. [1]",
        'They can talk, type or send a photo, whichever is easier. Captions show their words as they speak, if they want them.',
        "While they talk, the things they could tell us tick off, so they can see what's left without being asked.",
      ],
      // Mid-recording, so the checklist ticks off as the slide sits there.
      frame: 'help-recording',
      research: [
        {
          note: 'People in a conversation offer more than the question asks for. One open question lets them.',
          source: 'Google conversation design',
          href: 'https://developers.google.com/assistant/conversation-design/learn-about-conversation',
        },
      ],
    },
    {
      question: 'What we heard',
      points: [
        'The first thing we give back is proof we listened, in their own words. [2]',
        'They fix it by saying it, or with the pencil. There is no separate review step. [1]',
        'The turn ends when they confirm, not when they send. [3]',
      ],
      frame: 'help-summary',
      research: [
        {
          note: 'Saying back what was understood lets people catch mistakes straight away. Ask for a yes only when getting it wrong costs something, like a name.',
          source: 'Google conversation design, Confirmations',
          href: 'https://developers.google.com/assistant/conversation-design/confirmations',
        },
        {
          note: 'A short acknowledgement shows they were heard, and keeps the conversation flowing.',
          source: 'Google conversation design, Acknowledgements',
          href: 'https://developers.google.com/assistant/conversation-design/acknowledgements',
        },
        {
          note: "In conversation, something said only counts as understood once the other person shows they've got it. So the turn ends on their yes to the summary, not on what they send.",
          source: 'Clark and Brennan, Grounding in communication (1991)',
          href: 'https://en.wikipedia.org/wiki/Grounding_in_communication',
        },
      ],
    },
    {
      // What the summary left, the phone number, then where to start.
      question: "Only what's left, then one way on",
      points: [
        'We only ask what the summary left out: where they live and how old they are. [1]',
        'These are the minimum we need. Four questions cover 88% of the high-value benefits: where they live, their age, their health and when they need help. Their own words usually cover the last two, so we ask the first two, and they help our care experts too. [4]',
        'Each question says why we need it, and names our care experts, so they know a real person will use what they tell us. [3]',
        'Then a phone number, so we can keep in touch. They can leave it for later.',
        'Then one way on: book a call with a care expert, or do it later. [2]',
        "This is the gateway to our other AI features. Because these answers cover most of the high-value benefits, once the benefits calculator joins up we can give back a first idea of what they could get, straight away. That's where it all starts to connect. [4]",
      ],
      frame: 'help-start-talk',
      research: [
        {
          note: "Say as much as is needed, and no more. Asking for what they've already said breaks the rule people expect a conversation to follow.",
          source: "Grice's maxim of quantity, via Google conversation design",
          href: 'https://developers.google.com/assistant/conversation-design/learn-about-conversation',
        },
        {
          note: 'People judge an experience largely by how they felt at its peak and at its end. So it ends on their choice, not a form.',
          source: 'Laws of UX, Peak-end rule',
          href: 'https://lawsofux.com/peak-end-rule/',
        },
        {
          note: '88% of people would be more comfortable if a clinician reviewed AI summaries.',
          source: 'Consumer attitudes towards AI in digital health, 2026',
        },
        {
          note: 'Four questions cover 88% of the high-value benefits: where they live, their age, their health, and when they need help.',
          source:
            "James's benefits eligibility priority analysis, December 2025",
        },
      ],
    },
    {
      // No phone: the family's screens say nothing about the care expert's
      // side. Hidden for now.
      hidden: true,
      question: 'What does it mean for our care experts?',
      points: [
        'They start from what the family told us, read and organised, instead of working back from the onboarding form to make sense of it.',
        "It's in the family's own words, so it's more human, and the family has already checked it.",
        'They get a better picture of the more niche situations, rather than guessing from which forms someone starts to fill in.',
        "We tell families plainly what AI is for, in Keni's first message: helping our care experts read what they share, so they only tell us once. A real person always looks over it.",
      ],
      // Its source moved to Only what's left, which cites it.
    },
    {
      // The end: the key screens side by side, each live to click through.
      frames: [
        { frame: 'chat-v2', label: 'What we do, and the consent' },
        { frame: 'help-ask', label: 'The open question' },
        { frame: 'help-summary', label: 'Does this look right?' },
        { frame: 'help-age', label: 'How old they are' },
        { frame: 'help-start-talk', label: 'Where to start' },
      ],
    },
  ] satisfies WalkthroughSlide[],
};
